import EsriColor from '@arcgis/core/Color';
import Color from 'color';

export function colorTone(color: string): 'dark' | 'light' {
  if (color === 'transparent') {
    return 'light';
  }

  const isDark = Color(color).isDark();

  return isDark ? 'dark' : 'light';
}

export function colorIsDark(color: string): boolean {
  return colorTone(color) === 'dark';
}

export function colorIsLight(color: string): boolean {
  return colorTone(color) === 'light';
}

export function colorToRgbWithOpacity(color: string, opacity: number): number[] {
  const esriColor = EsriColor.fromHex(color);
  return [...esriColor.toRgb(), opacity];
}

export function colorToEsriColorWithOpacity(color: string, opacity: number): EsriColor {
  const colorType = isHexOrRGB(color);

  let esriColor: EsriColor;

  switch (colorType) {
    case 'hex':
      esriColor = EsriColor.fromHex(color);
      esriColor.a = opacity;
      break;
    case 'rgb':
      esriColor = EsriColor.fromRgb(color);
      break;
  }

  return esriColor;
}

export function isHexOrRGB(color: string): 'hex' | 'rgb' | 'invalid' {
  // Hex code regex (supports #RRGGBB or #RGB)
  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

  // RGB regex (supports rgb(x, y, z) where x, y, z are integers between 0 and 255)
  const rgbRegex = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/;

  if (hexRegex.test(color)) {
    return 'hex';
  } else if (rgbRegex.test(color)) {
    const result = color.match(rgbRegex);
    if (result) {
      const r = parseInt(result[1], 10);
      const g = parseInt(result[2], 10);
      const b = parseInt(result[3], 10);
      if (r >= 0 && r <= 255 && g >= 0 && g <= 255 && b >= 0 && b <= 255) {
        return 'rgb';
      }
    }
  }

  return 'invalid';
}

export function convertOpacityToHex(opacity: number): string {
  if (opacity < 0 || opacity > 1) {
    throw new Error('Opacity must be between 0 and 1');
  }

  // Convert the opacity to a number between 0 and 255
  const hexValue = Math.round(opacity * 255);

  // Convert the number to a hex string and pad with leading zero if necessary
  return hexValue.toString(16).padStart(2, '0').toUpperCase();
}

/**
 * Translates a hex or RGBA color code to RGBA format
 *
 * @param color A hex or rgb color
 * @param opacity Number between 0 and 1
 * @returns Color in RGBA format
 */

export function hexToRgb(color: string, opacity: number): string {
  const colorType = isHexOrRGB(color); // Use the function to determine if hex or rgb

  switch (colorType) {
    case 'hex':
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
      if (result) {
        return `rgba(${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(
          result[3],
          16,
        )},${opacity})`;
      }
      break;
    case 'rgb':
      return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
  }
}

export function hexToRgba(hexColor: string, opacity: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor);
  return `rgba(${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(
    result[3],
    16,
  )}, ${opacity})`;
}

export function hexToRgbArray(color: string): number[] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
}
